import React, { useEffect, useState, useMemo } from 'react';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { ApiError, api } from '@/lib/api';
import { useUrlState } from '@/lib/useUrlState';
import StaffEditModal from './StaffEditModal';

const ROLE_RU = {
  administrator: 'Администратор',
  teacher:       'Учитель',
  employee:      'Сотрудник',
};

const URL_SCHEMA = { role: '', active: '1', q: '', edit: '' };

export default function StaffPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canWriteAdmin    = can('staff.administrators', 'write');
  const canWriteTeacher  = can('staff.teachers',       'write');
  const canWriteEmployee = can('staff.employees',      'write');
  const canCreate        = canWriteAdmin || canWriteTeacher || canWriteEmployee;

  const [params, setParams] = useUrlState(URL_SCHEMA);
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  const reload = () => {
    setLoading(true); setError(null);
    const qs = new URLSearchParams();
    if (params.role)   qs.set('role', params.role);
    if (params.active) qs.set('active', params.active);
    if (params.q)      qs.set('q', params.q);
    api.get('/api/staff' + (qs.toString() ? '?' + qs : ''))
      .then((res) => setRows(res.rows || []))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [params.role, params.active, params.q]);

  const editing = useMemo(() => {
    if (!params.edit) return null;
    if (params.edit === 'new') return 'new';
    const id = parseInt(params.edit, 10);
    return rows.find((r) => r.id === id) || null;
  }, [params.edit, rows]);

  return (
    <>
      <Topbar
        breadcrumb={['Администрирование', 'Сотрудники']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />
      <div className="ref-page">
        <div className="ref-toolbar">
          <span className="ref-toolbar__title">
            Сотрудники {!loading && <span className="muted">· {rows.length}</span>}
          </span>
          <input
            className="ref-toolbar__search"
            placeholder="Поиск: ФИО, email, UPN"
            value={params.q || ''}
            onChange={(e) => setParams({ q: e.target.value })}
            style={{
              marginLeft: 16, height: 30, padding: '0 10px',
              border: '1px solid var(--border-strong)', borderRadius: 6,
              fontSize: 13, background: 'var(--panel)', color: 'var(--fg)',
              minWidth: 240,
            }}
          />
          <select
            value={params.role || ''}
            onChange={(e) => setParams({ role: e.target.value })}
            style={{
              marginLeft: 8, height: 30, padding: '0 8px',
              border: '1px solid var(--border-strong)', borderRadius: 6,
              fontSize: 13, background: 'var(--panel)', color: 'var(--fg)',
            }}
          >
            <option value="">Все роли</option>
            <option value="administrator">Администраторы</option>
            <option value="teacher">Учителя</option>
            <option value="employee">Сотрудники</option>
          </select>
          <select
            value={params.active || '1'}
            onChange={(e) => setParams({ active: e.target.value })}
            style={{
              marginLeft: 8, height: 30, padding: '0 8px',
              border: '1px solid var(--border-strong)', borderRadius: 6,
              fontSize: 13, background: 'var(--panel)', color: 'var(--fg)',
            }}
          >
            <option value="1">Активные</option>
            <option value="0">В архиве</option>
            <option value="">Все</option>
          </select>
          <div className="ref-toolbar__spacer" />
          <button
            className="btn btn--primary btn--sm"
            onClick={() => setParams({ edit: 'new' })}
            disabled={!canCreate}
            title={canCreate ? undefined : 'Нет прав на создание'}
          >
            <Icon name="plus" size={13} /> Добавить сотрудника
          </button>
        </div>

        <div className="ref-content">
          {error && (
            <div style={{ color: 'var(--red-fg)', padding: 16 }}>Ошибка: {error}</div>
          )}
          {loading ? (
            <div className="ref-empty">Загрузка…</div>
          ) : rows.length === 0 ? (
            <div className="ref-empty">Никого не найдено. Поправь фильтры или добавь первого сотрудника.</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>ФИО</th>
                    <th style={{ width: 140 }}>Роль</th>
                    <th style={{ width: 240 }}>Email</th>
                    <th style={{ width: 200 }}>AD</th>
                    <th style={{ width: 120 }}>Классы</th>
                    <th style={{ width: 100 }}>Статус</th>
                    <th style={{ width: 60 }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => (
                    <tr key={u.id} onClick={() => setParams({ edit: String(u.id) })} style={{ cursor: 'pointer' }}>
                      <td><span style={{ fontWeight: 500 }}>{u.name}</span></td>
                      <td>{ROLE_RU[u.role] || u.role}</td>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {u.email || <span style={{ color: 'var(--fg-faint)' }}>—</span>}
                      </td>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {u.adUserPrincipalName || <span style={{ color: 'var(--fg-faint)' }}>—</span>}
                      </td>
                      <td>{u.classesCount || ''}</td>
                      <td>
                        {u.active
                          ? <span className="sb sb--active">Активен</span>
                          : <span className="sb sb--archived">Архив</span>}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="ref-row-actions">
                          <button
                            onClick={() => setParams({ edit: String(u.id) })}
                            title="Редактировать"
                          >
                            <Icon name="edit" size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <StaffEditModal
          mode={editing === 'new' ? 'create' : 'edit'}
          staff={editing === 'new' ? null : editing}
          onClose={() => setParams({ edit: '' })}
          onSaved={(u) => {
            setRows((prev) => {
              const idx = prev.findIndex((r) => r.id === u.id);
              if (idx >= 0) { const next = prev.slice(); next[idx] = u; return next; }
              return [u, ...prev];
            });
          }}
          onDeleted={(id) => setRows((prev) => prev.filter((r) => r.id !== id))}
        />
      )}
    </>
  );
}
